import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface SectionHeadingProps {
    id: string
    index: string
    label: string
    title: ReactNode
    lead?: ReactNode
    action?: ReactNode
    tone?: 'dark' | 'light'
    className?: string
}

interface AccentProps {
    children: ReactNode
    className?: string
}

interface EyebrowProps {
    index: string
    label: string
    tone?: 'dark' | 'light'
}

/** Numbered mono label that opens every section. */
export const Eyebrow: FC<EyebrowProps> = ({ index, label, tone = 'dark' }) => {
    const dark = tone === 'dark'

    return (
        <div className={cn('flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em]', dark ? 'text-steel' : 'text-ink/80')}>
            <span className={dark ? 'text-ember' : 'text-ink'}>
                {index}
            </span>
            <span aria-hidden='true' className={cn('h-px w-10', dark ? 'bg-white/15' : 'bg-ink/25')} />
            <span>
                {label}
            </span>
        </div>
    )
}

/** Serif italic emphasis used inside the condensed headlines. */
export const Accent: FC<AccentProps> = ({ children, className }) => {
    return (
        <em className={cn('font-serif font-normal italic tracking-[-0.01em]', className)}>
            {children}
        </em>
    )
}

const SectionHeading: FC<SectionHeadingProps> = ({
    id,
    index,
    label,
    title,
    lead,
    action,
    tone = 'dark',
    className
}) => {
    const dark = tone === 'dark'

    return (
        <div className={cn('grid gap-8 lg:grid-cols-12 lg:items-end lg:gap-10', className)}>
            <div className='lg:col-span-8'>
                <Eyebrow index={index} label={label} tone={tone} />

                <h2
                    id={id}
                    className={cn(
                        'mt-6 text-balance text-[clamp(2.5rem,5.6vw,5rem)] font-semibold leading-[0.94] tracking-[-0.032em] stretch-condensed',
                        dark ? 'text-bone' : 'text-ink'
                    )}
                >
                    {title}
                </h2>
            </div>

            {(lead || action) && (
                <div className='lg:col-span-4 lg:pb-2'>
                    {lead && (
                        <p className={cn('text-base leading-relaxed md:text-[17px]', dark ? 'text-steel' : 'text-ink/70')}>
                            {lead}
                        </p>
                    )}

                    {action && (
                        <div className='mt-6'>
                            {action}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default SectionHeading
